import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  atalhoSemFluxoNavState, emergencyNavState, manualActionNavState,
  resolverAtalhoDeFluxo, resolverAtalhoDeSemSinal, type ResultadoAtalho, type TarpLevel,
} from '@/domain';
import { AcionamentosManuais } from '@/components/tarp';
import { BannerArea } from '@/components/BannerArea';
import { PageHeader } from '@/ui';
import { FalhaDeCarga } from '@/components/FalhaDeCarga';
import { useEmailSuporteApp } from '@/hooks/useEmailSuporteApp';
import { useAlertStore } from '@/stores/alertStore';
import { useDamScope } from '@/hooks/useDamScope';
import { nivelAnmSemaforo } from '@/domain/estrutura';
import { useM1Data } from './useM1Data';
import SemaphoreCard from './SemaphoreCard';
import StatCards from './StatCards';
import AnomalyList from './AnomalyList';
import TarpSummaryCard from './TarpSummaryCard';
import ThresholdsPorEstrutura from '@/components/instrumentos/ThresholdsPorEstrutura';
import { useFaixasCanal } from '@/hooks/useFaixasCanal';
import EmergencyButton from './EmergencyButton';
import { TechCoverageCard } from './TechCoverageCard';

export default function M1Dashboard() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const setOverall = useAlertStore((s) => s.setOverall);
  const {
    instruments, dams, readings, byLevel, badInstruments, anomaliasNotificaveis, nivelOverall,
    instrumentosNormais, tiltResumo, eventosUlt24h, lastReading,
    lastUpdateTs, tarpsResumo,
    isLoading, error,
  } = useM1Data();
  // GUPM-270 — régua por eixo do tiltímetro, para a tabela dedicada do painel.
  const { faixasCanal } = useFaixasCanal();
  // GUPM-336: contato do suporte na tela de falha (vem do banco).
  const emailSuporte = useEmailSuporteApp();
  // GUPM-221 — o semáforo do dashboard usa a MESMA fonte do header: o status da
  // estrutura (instrumentos + TARPs abertas + emergência + declaração ANM do RT).
  // `nivelAnmSemaforo` colapsa Observação em Normal: ocorrência automática é nível
  // de CONTROLE e não declara nível de barragem — o semáforo só pinta com
  // declaração do RT ou emergência. Observação continua visível no chip do header.
  const { consolidado } = useDamScope();
  const nivelAnm = nivelAnmSemaforo(consolidado);

  useEffect(() => {
    setOverall(nivelOverall);
  }, [nivelOverall, setOverall]);

  // GUPM-185: abre o fluxo TARP pré-preenchido a partir de um instrumento anômalo.
  // Canal efêmero via router state (não query string). Reusado pelo "Precisa da sua
  // atenção" e pelo atalho "→ Fluxo" dos Thresholds por Estrutura (GUPM-206).
  //
  // C-88 — resolve pelo ID (o código se repete entre barragens) e, quando não há fluxo a
  // abrir, leva o MOTIVO para a tela de TARP em vez de navegar pelado: em 14/10/2026 o
  // clique num tiltímetro em Emergência caía na lista sem aviso nenhum.
  const abrirFluxo = (instrumentId: string) => {
    // GUPM-303 — procura em `instruments`, não em `badInstruments`. Aquela lista só tem
    // anomalia de LEITURA, e o tiltímetro mudo vem de outra (`computeSemSinal`).
    const nivelDe = (i: { id: string }): TarpLevel | null => {
      for (const b of badInstruments) if (b.kind === 'nivel' && b.instrument.id === i.id) return b.level;
      return null;
    };
    irParaTarp(instrumentId, resolverAtalhoDeFluxo(instrumentId, instruments, nivelDe));
  };

  /** Navega com o fluxo pronto ou, sem fluxo, com o motivo para a tela de TARP avisar (C-88). */
  const irParaTarp = (instrumentId: string, r: ResultadoAtalho) => {
    if (r.ok) {
      navigate('/tarp', { state: { tarpAutoOpen: r.ctx } });
      return;
    }
    console.warn('[TARP] atalho sem fluxo', { instrumentId, motivo: r.motivo });
    navigate('/tarp', { state: atalhoSemFluxoNavState(r.motivo, r.instrumentCode) });
  };

  /**
   * GUPM-303 — abre o fluxo da TARP de PERDA DE SINAL. Recebe o id do instrumento em vez
   * do código porque `TIL-01` existe nas três barragens: resolver por código despacharia
   * equipe para a estrutura errada. Deriva o protocolo pelo gatilho de perda de sinal
   * (`TARP-PS-TILT`; o card de sem sinal só existe para tiltímetro — GUPM-303).
   */
  const abrirFluxoSemSinal = (instrumentId: string) => {
    irParaTarp(instrumentId, resolverAtalhoDeSemSinal(instrumentId, instruments));
  };

  if (isLoading) {
    return (
      <div>
        <p className="text-jf-muted text-sm">Carregando dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {/* GUPM-336: a dica de `supabase start` só aparece em DEV. */}
        <FalhaDeCarga titulo="Erro ao carregar dados" erro={error} emailSuporte={emailSuporte} />
      </div>
    );
  }

  return (
    <div>
      {/* Cobertura técnica (GUPM-199) no slot de ações do cabeçalho: na mesma linha
          do título (GUPM-221), em vez de uma faixa própria que só criava vão vertical. */}
      <PageHeader title="Painel de Monitoramento" actions={<TechCoverageCard />} />

      <BannerArea modulo="M1" />

      {/* "Precisa da sua atenção" — instrumentos que ainda PEDEM AÇÃO (auto-open
          GUPM-185), com texto por perfil (GUPM-206). Coberto por TARP em curso sai
          da lista (GUPM-221); escalada e prazo vencido nunca são suprimidos. */}
      <AnomalyList
        badInstruments={anomaliasNotificaveis}
        profile={profile}
        onTarp={abrirFluxo}
        onTarpSemSinal={abrirFluxoSemSinal}
      />

      {/* D2 — Acionamentos manuais na home (fiel ao V8). Emergência abre a TARP-EMG;
          os demais roteiam ao M3 (mesmo painel + fluxo completo). */}
      <AcionamentosManuais
        className="mb-3"
        onAcionar={(code) =>
          navigate('/tarp', {
            state: code === 'TARP-EMG' ? emergencyNavState() : manualActionNavState(code),
          })
        }
      />

      {/* D3 — Resumo das TARPs. */}
      <TarpSummaryCard resumo={tarpsResumo} />

      {/* D8 — nível ANM consolidado com o timer do ciclo do cron integrado. */}
      <SemaphoreCard status={nivelAnm} byLevel={byLevel} lastUpdateTs={lastUpdateTs} />

      {/* D4/D5/D6/D7 — stat cards. */}
      <StatCards
        instrumentosNormais={instrumentosNormais}
        tiltResumo={tiltResumo}
        eventosUlt24h={eventosUlt24h}
        lastReading={lastReading}
      />

      {(profile === 'tecnico_responsavel' || profile === 'admin') && (
        <ThresholdsPorEstrutura
          instruments={instruments}
          readings={readings}
          dams={dams}
          faixasCanal={faixasCanal}
          onFluxo={abrirFluxo}
          className="mb-3"
        />
      )}

      {/* D9 — botão grande abre a TARP-EMG no M3 (mesmo contrato do header). */}
      <EmergencyButton
        status={nivelAnm}
        onClick={() => navigate('/tarp', { state: emergencyNavState() })}
      />
    </div>
  );
}
